import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Save, Trash2, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { z } from "zod";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const kpiSchema = z.object({
  name: z.string().min(2).max(100),
  unit: z.enum(["₹", "%", "#"]),
  min_band: z.number().nullable(),
  max_band: z.number().nullable(),
  delta_warn: z.number().nullable(),
  owner_user_id: z.string().uuid(),
  evidence_required: z.boolean(),
});

interface CompanyMember { user_id: string; profile_name?: string; }

const EditKPI = () => {
  const { id, kpiId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [companyName, setCompanyName] = useState("");
  const [form, setForm] = useState({
    name: "", unit: "#" as "₹" | "%" | "#", min_band: "", max_band: "",
    delta_warn: "", owner_user_id: "", evidence_required: false,
  });

  useEffect(() => { loadData(); }, [id, kpiId]);

  const loadData = async () => {
    try {
      const [kpiRes, membersRes, companyRes] = await Promise.all([
        supabase.from("kpis").select("*").eq("id", kpiId).single(),
        supabase.from("company_members").select("user_id").eq("company_id", id),
        supabase.from("companies").select("name").eq("id", id).single(),
      ]);

      if (kpiRes.error) throw kpiRes.error;
      if (membersRes.error) throw membersRes.error;
      setCompanyName(companyRes.data?.name || "");

      const kpiData = kpiRes.data;
      setForm({
        name: kpiData.name, unit: kpiData.unit as "₹" | "%" | "#",
        min_band: kpiData.min_band?.toString() || "", max_band: kpiData.max_band?.toString() || "",
        delta_warn: kpiData.delta_warn?.toString() || "", owner_user_id: kpiData.owner_user_id,
        evidence_required: kpiData.evidence_required || false,
      });

      const membersWithProfiles = await Promise.all(
        (membersRes.data || []).map(async (m) => {
          const { data: profile } = await supabase.from("profiles").select("name").eq("id", m.user_id).single();
          return { user_id: m.user_id, profile_name: profile?.name };
        })
      );
      setMembers(membersWithProfiles);
    } catch (error: any) {
      toast.error(error.message);
      navigate(`/app/companies/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const validated = kpiSchema.parse({
        ...form,
        min_band: form.min_band ? parseFloat(form.min_band) : null,
        max_band: form.max_band ? parseFloat(form.max_band) : null,
        delta_warn: form.delta_warn ? parseFloat(form.delta_warn) : null,
      });
      const { error } = await supabase.from("kpis").update({
        name: validated.name, unit: validated.unit, min_band: validated.min_band,
        max_band: validated.max_band, delta_warn: validated.delta_warn,
        owner_user_id: validated.owner_user_id, evidence_required: validated.evidence_required,
      }).eq("id", kpiId);
      if (error) throw error;
      toast.success("KPI updated successfully");
      navigate(`/app/companies/${id}/kpis/${kpiId}`);
    } catch (err: any) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
      else toast.error(err.message || "Failed to update KPI");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await supabase.from("kpi_values").delete().eq("kpi_id", kpiId);
      const { error } = await supabase.from("kpis").delete().eq("id", kpiId);
      if (error) throw error;
      toast.success("KPI deleted successfully");
      navigate(`/app/companies/${id}`);
    } catch (error: any) {
      toast.error(error.message || "Failed to delete KPI");
    } finally { setDeleting(false); }
  };

  if (loading) {
    return (
      <AppShell title="Loading..." showBack backTo={`/app/companies/${id}/kpis/${kpiId}`}>
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Edit KPI" subtitle={companyName} showBack backTo={`/app/companies/${id}/kpis/${kpiId}`} companyId={id} companyName={companyName}>
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Edit KPI</CardTitle>
              <CardDescription>Modify KPI settings and target bands</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">KPI name</Label>
                  <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={100} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select value={form.unit} onValueChange={(v: "₹" | "%" | "#") => setForm({ ...form, unit: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="₹">₹ (Currency)</SelectItem>
                      <SelectItem value="%">% (Percentage)</SelectItem>
                      <SelectItem value="#"># (Number)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min target</Label>
                    <Input type="number" step="any" placeholder="Optional" value={form.min_band} onChange={(e) => setForm({ ...form, min_band: e.target.value })} className="rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>Max target</Label>
                    <Input type="number" step="any" placeholder="Optional" value={form.max_band} onChange={(e) => setForm({ ...form, max_band: e.target.value })} className="rounded-xl" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Delta warning</Label>
                  <Input type="number" step="any" placeholder="Optional" value={form.delta_warn} onChange={(e) => setForm({ ...form, delta_warn: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select value={form.owner_user_id} onValueChange={(v) => setForm({ ...form, owner_user_id: v })}>
                    <SelectTrigger className="rounded-xl"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.profile_name || "Unknown"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
                  <div><Label>Require evidence</Label><p className="text-xs text-muted-foreground">Ask for proof/link</p></div>
                  <Switch checked={form.evidence_required} onCheckedChange={(c) => setForm({ ...form, evidence_required: c })} />
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => navigate(`/app/companies/${id}/kpis/${kpiId}`)} className="flex-1 order-2 sm:order-1 rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={saving} className="flex-1 order-1 sm:order-2 rounded-xl"><Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save changes"}</Button>
                </div>
                <div className="pt-4 border-t border-border">
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="destructive" className="w-full rounded-xl" type="button"><Trash2 className="h-4 w-4 mr-2" />Delete KPI</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete KPI?</AlertDialogTitle>
                        <AlertDialogDescription>This will permanently delete this KPI and all its historical values.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{deleting ? "Deleting..." : "Delete"}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
};

export default EditKPI;
